import { ReactNode } from 'react';

import { Box } from 'components/Box';
import { ListingCounts } from 'components/Listing/ListingCounts';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import {
  getContentFirstRealBlockIndex,
  getContentHasBodyHeader,
  getContentListingCounts,
  getContentShowListingCounts,
} from 'modules/cms/helpers';
import { mapCmsBlocksForBody } from 'modules/cms/mappers';
import { sectionPadding } from 'theme/theme';

import { CmsBodyHeader } from './CmsBodyHeader';
import { CmsBodyHolder, CmsBodyHolderWidth } from './CmsBodyHolder';
import { CmsBodyMainContent } from './CmsBodyMainContent';

type Props = {
  content: CmsApiPage;
  headerImage: ReactNode | undefined;
  padBottom: boolean;
  padTop: boolean;
  socialButtons: ReactNode | null | undefined;
  subscribeButton: ReactNode | null | undefined;
};

export function CmsBody({
  content,
  headerImage,
  padBottom,
  padTop,
  socialButtons,
  subscribeButton,
}: Props) {
  const showListingCounts = getContentShowListingCounts({ content });
  const listingCounts = getContentListingCounts({ content });
  const hasHeader = getContentHasBodyHeader({ content });
  const firstRealBlockIndex = getContentFirstRealBlockIndex({ content });

  const mappedBlocks = mapCmsBlocksForBody({ content });

  const firstRealBlock = mappedBlocks[firstRealBlockIndex];
  const firstRealBlockHasBackground = Boolean(
    mappedBlocks?.length && firstRealBlock?.hasBackground,
  );

  const headerBodyHolderWidth =
    content.layout === 'NARROW'
      ? CmsBodyHolderWidth.NARROW_LAYOUT
      : CmsBodyHolderWidth.PAGE_LAYOUT;

  return (
    <Box
      component="main"
      ml="auto"
      mr="auto"
      mb={!padBottom ? [-36, null, null, -54] : undefined}
      data-qa-id="content-container"
    >
      {(hasHeader || headerImage || showListingCounts) && (
        <header>
          {hasHeader && (
            <Box m={sectionPadding}>
              <CmsBodyHolder $width={headerBodyHolderWidth}>
                <CmsBodyHeader content={content} />
              </CmsBodyHolder>
            </Box>
          )}

          {headerImage && (
            <Box m={sectionPadding}>
              <CmsBodyHolder $width={headerBodyHolderWidth}>
                {headerImage}
              </CmsBodyHolder>
            </Box>
          )}

          {showListingCounts && listingCounts ? (
            <ListingCounts
              darkMode={firstRealBlockHasBackground}
              listingCounts={listingCounts}
            />
          ) : null}
        </header>
      )}

      <Box
        marginLeft={sectionPadding}
        marginRight={sectionPadding}
        marginTop={padTop ? sectionPadding : undefined}
      >
        <CmsBodyMainContent
          content={content}
          mappedBlocks={mappedBlocks}
          socialButtons={socialButtons}
          subscribeButton={subscribeButton}
        />
      </Box>
    </Box>
  );
}
